import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadTemplateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService } from '@services/sheltersService';

interface ImportJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileName: string;
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  errorCount: number;
  errors: ImportError[];
  createdAt: string;
  completedAt?: string;
}

interface ImportError {
  row: number;
  field: string;
  message: string;
  value?: string;
}

const ShelterImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ImportJob | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    loadImportJobs();
  }, []);

  const loadImportJobs = async () => {
    try {
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      const response = await shelterService.getImportJobs(user.shelterId);
      // Asigură-te că importJobs este întotdeauna un array
      const jobsData = Array.isArray(response) ? response : (response?.data || []);
      setImportJobs(jobsData);
    } catch (error) {
      console.error('Error loading import jobs:', error);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setActiveStep(1);
      
      // Parse CSV and show preview
      try {
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          setPreviewData([]);
          return;
        }
        
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        // Parse data rows (max 5 for preview)
        const dataRows = lines.slice(1, 6).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });
        
        setPreviewData(dataRows);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        setPreviewData([]);
      }
    }
  };

  const handleStartImport = async () => {
    if (!selectedFile || !user?.shelterId) return;

    try {
      setActionError(null);
      setLoading(true);
      const response = await shelterService.startImport(user.shelterId, selectedFile);
      setActiveStep(2);
      loadImportJobs(); // Refresh jobs list
    } catch (error) {
      console.error('Error starting import:', error);
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : '';
      setActionError(msg || 'Nu am putut porni importul. Incearca din nou.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setActionError(null);
      const blob = await shelterService.downloadImportTemplate();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-import-animale.csv';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      setActionError('Nu am putut descarca sablonul. Verifica conexiunea si incearca din nou.');
    }
  };

  const handleViewErrors = (job: ImportJob) => {
    setSelectedJob(job);
    setErrors(job.errors);
    setErrorDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <SuccessIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <UploadIcon />;
    }
  };

  const steps = [
    {
      label: 'Selectează Fișierul',
      description: 'Alege fișierul CSV sau Excel cu animalele'
    },
    {
      label: 'Previzualizare',
      description: 'Verifică datele înainte de import'
    },
    {
      label: 'Import',
      description: 'Procesează și importă animalele'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Import Animale
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Importă animale în masă din fișier CSV sau Excel
        </Typography>
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Download Template */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DownloadTemplateIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Template CSV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Descarcă template-ul pentru a vedea formatul corect
              </Typography>
            </Box>
          </Stack>
        </CardContent>
        <CardActions>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Descarcă Template
          </Button>
        </CardActions>
      </Card>

      {/* Import Steps */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            Import Animale
          </Typography>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {step.description}
                  </Typography>
                  
                  {index === 0 && (
                    <Box>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="file-input"
                      />
                      <label htmlFor="file-input">
                        <Button
                          variant="contained"
                          component="span"
                          startIcon={<UploadIcon />}
                        >
                          Alege Fișier
                        </Button>
                      </label>
                    </Box>
                  )}

                  {index === 1 && selectedFile && (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Fișier selectat: {selectedFile.name}
                      </Alert>
                      {previewData.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {Object.keys(previewData[0] || {}).map((header) => (
                                  <TableCell key={header}>{header}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {previewData.map((row, idx) => (
                                <TableRow key={idx}>
                                  {Object.values(row).map((value, cellIdx) => (
                                    <TableCell key={cellIdx}>{String(value)}</TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="warning">
                          Nu s-au putut încărca datele pentru previzualizare
                        </Alert>
                      )}
                      <Button
                        variant="contained"
                        onClick={handleStartImport}
                        disabled={loading}
                        sx={{ mt: 2 }}
                      >
                        {loading ? 'Procesez...' : 'Începe Import'}
                      </Button>
                    </Box>
                  )}

                  {index === 2 && (
                    <Box>
                      {loading ? (
                        <Box>
                          <LinearProgress />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Procesez fișierul...
                          </Typography>
                        </Box>
                      ) : (
                        <Alert severity="success">
                          Import finalizat cu succes!
                        </Alert>
                      )}
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Import History */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            Istoric Importuri
          </Typography>
          
          {importJobs.length === 0 ? (
            <Alert severity="info">
              Nu ai importuri anterioare
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fișier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Înregistrări</TableCell>
                    <TableCell>Succes</TableCell>
                    <TableCell>Erori</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importJobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.fileName}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.totalRecords}</TableCell>
                      <TableCell>{job.successCount}</TableCell>
                      <TableCell>{job.errorCount}</TableCell>
                      <TableCell>
                        {new Date(job.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {job.errorCount > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleViewErrors(job)}
                          >
                            <ViewIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Errors Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Erori de Import</DialogTitle>
        <DialogContent>
          {errors.length === 0 ? (
            <Alert severity="info">Nu există erori</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rând</TableCell>
                    <TableCell>Câmp</TableCell>
                    <TableCell>Eroare</TableCell>
                    <TableCell>Valoare</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.map((error, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{error.row}</TableCell>
                      <TableCell>{error.field}</TableCell>
                      <TableCell>{error.message}</TableCell>
                      <TableCell>{error.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShelterImportPage;
